import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useBrandKit } from '../../contexts/BrandKitContext';
import { useNotification } from '../../contexts/NotificationContext';
import BrandKitForm from './BrandKitForm';
import BrandKitCard from './BrandKitCard';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiLoader } = FiIcons;

export default function BrandKitManager() {
  const { brandKits, loading, createBrandKit, updateBrandKit, deleteBrandKit } = useBrandKit();
  const { showSuccess, showError } = useNotification();
  const [showForm, setShowForm] = useState(false);
  const [editingKit, setEditingKit] = useState(null);

  const handleCreate = async (data) => {
    try {
      console.log('Creating brand kit with data:', data);
      const newBrandKit = await createBrandKit(data);
      console.log('Brand kit created successfully:', newBrandKit);
      showSuccess('Brand kit created successfully');
      setShowForm(false);
      setEditingKit(null);
    } catch (error) {
      console.error('Failed to create brand kit:', error);
      showError('Failed to create brand kit: ' + error.message);
    }
  };

  const handleUpdate = async (data) => {
    try {
      console.log('Updating brand kit:', editingKit?.id, 'with data:', data);
      const updatedBrandKit = await updateBrandKit(editingKit.id, data);
      console.log('Brand kit updated successfully:', updatedBrandKit);
      showSuccess('Brand kit updated successfully');
      setShowForm(false);
      setEditingKit(null);
    } catch (error) {
      console.error('Failed to update brand kit:', error);
      showError('Failed to update brand kit: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this brand kit?')) {
      try {
        console.log('Deleting brand kit:', id);
        await deleteBrandKit(id);
        console.log('Brand kit deleted successfully');
        showSuccess('Brand kit deleted successfully');
      } catch (error) {
        console.error('Failed to delete brand kit:', error);
        showError('Failed to delete brand kit: ' + error.message);
      }
    }
  };

  const handleEdit = (kit) => {
    console.log('Editing brand kit:', kit);
    setEditingKit(kit);
    setShowForm(true);
  };

  const handleCancel = () => {
    console.log('Cancelling form');
    setShowForm(false);
    setEditingKit(null);
  };

  const handleNewBrandKit = () => {
    console.log('Creating new brand kit');
    setEditingKit(null);
    setShowForm(true);
  };

  console.log('BrandKitManager state:', {
    brandKits: brandKits?.length || 0,
    loading,
    showForm,
    editingKit: editingKit?.id || null
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <SafeIcon icon={FiLoader} className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Brand Kits</h2>
          <p className="text-gray-600">Manage your brand assets and styling</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleNewBrandKit}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
          New Brand Kit
        </motion.button>
      </div>

      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-md p-6"
        >
          <BrandKitForm
            initialData={editingKit}
            onSubmit={editingKit ? handleUpdate : handleCreate}
            onCancel={handleCancel}
          />
        </motion.div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {brandKits && brandKits.length > 0 ? (
          brandKits.map((kit) => (
            <BrandKitCard
              key={kit.id}
              brandKit={kit}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))
        ) : null}
      </div>

      {(!brandKits || brandKits.length === 0) && !showForm && (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg mb-4">No brand kits created yet</p>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleNewBrandKit}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <SafeIcon icon={FiPlus} className="w-5 h-5 mr-2" />
            Create Your First Brand Kit
          </motion.button>
        </div>
      )}
    </div>
  );
}